import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Loader2, Check, ArrowLeft } from 'lucide-react';
import { btn, btnPrimary, input } from './ui';

// S&S Activewear + SanMar garment search for the store editor (GET /catalog/search).
// Step 1: results list → Step 2: pick colors → onPick(normalised garment). Wholesale is shown for reference only.
export const CatalogSearch = ({ onPick, onCancel }) => {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(null);
  const [picked, setPicked] = useState([]);
  const tRef = useRef(null);

  useEffect(() => {
    if (tRef.current) clearTimeout(tRef.current);
    const query = q.trim();
    if (query.length < 2) { setRows([]); return; }
    tRef.current = setTimeout(async () => {
      setLoading(true);
      try { const r = await axios.get('/catalog/search', { params: { q: query, limit: 8 } }); setRows(Array.isArray(r.data) ? r.data : []); }
      catch { toast.error('Catalog search failed'); }
      finally { setLoading(false); }
    }, 350);
    return () => tRef.current && clearTimeout(tRef.current);
  }, [q]);

  const [verifying, setVerifying] = useState(false);
  const choose = async (r) => {
    setSel(r); setPicked((r.colors || []).slice(0, 12).map((c) => c.colorName));
    if (r.supplier !== 'SanMar') return;
    // SanMar: swap in CDN-verified photos per colour so no colour ships with the "Image Not Available" tile.
    setVerifying(true);
    try {
      const { data } = await axios.get(`/catalog/sanmar/${encodeURIComponent(r.styleID)}/images`);
      const byName = Object.fromEntries((data.colors || []).map((c) => [c.colorName, c]));
      setSel((cur) => (cur && cur.styleID === r.styleID ? { ...cur, styleImageUrl: data.styleImageUrl || cur.styleImageUrl, colors: (cur.colors || []).map((c) => (byName[c.colorName] ? { ...c, ...byName[c.colorName] } : c)) } : cur));
    } catch { /* keep feed images */ } finally { setVerifying(false); }
  };
  const toggle = (n) => setPicked((p) => (p.includes(n) ? p.filter((x) => x !== n) : [...p, n]));
  const confirm = () => {
    const colors = (sel.colors || []).filter((c) => picked.includes(c.colorName));
    if (!colors.length) { toast.error('Select at least one color'); return; }
    // SanMar titles already carry brand + style ("Port & Co Core Cotton Tee. PC54") — normalise to a clean product name.
    let title = String(sel.styleName || '').replace(new RegExp(`\\s*${String(sel.styleID).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*$`, 'i'), '').replace(/[.\s]+$/, '').trim();
    if (title && !title.toLowerCase().startsWith(String(sel.brandName || '').toLowerCase().slice(0, 6))) title = `${sel.brandName} ${title}`.trim();
    // Supplier descriptions are raw catalog text (missing spaces, fabric/colour dumps) — keep the first readable sentences.
    const desc = String(sel.description || '').replace(/\s+/g, ' ').replace(/([a-z])\.([A-Z0-9])/g, '$1. $2').trim();
    // Keep the marketing sentence(s); drop everything from the fabric spec / colour dump onward.
    const cut = desc.search(/\d+(\.\d+)?-?\s?(ounce|oz)|\d+\/\d+\s|\d+%|\(\s*[A-Z][a-z]+\s[A-Z]/);
    let short = (cut > 20 ? desc.slice(0, cut) : desc).trim().slice(0, 280);
    if (short.length === 280) short = short.slice(0, short.lastIndexOf(' ')).replace(/[,;:\s]+$/, '') + '…';
    short = short.replace(/[,;:(\s]+$/, '');
    onPick({ supplier: sel.supplier, brand: sel.brandName, style: sel.styleID, name: title || `${sel.brandName} ${sel.styleID}`, description: short,
      sizes: sel.sizes || [], wholesale_from: sel.wholesaleFrom || null, image_url: colors[0].imageUrl || sel.styleImageUrl || '',
      colors: colors.map((c) => ({ name: c.colorName, hex: c.hex || '#333333', image_url: c.imageUrl || sel.styleImageUrl || '', views: c.views || null })) });
  };

  if (sel) {
    return (
      <div style={{ display: 'grid', gap: 10 }} data-testid="catalog-colors">
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" style={btn} onClick={() => setSel(null)} data-testid="catalog-back"><ArrowLeft size={13} /> Back</button>
          {sel.styleImageUrl && <img src={sel.styleImageUrl} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, background: '#fff' }} />}
          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ fontWeight: 700 }}>{sel.brandName} {sel.styleID}</div>
            <div style={{ fontSize: 12, color: '#A1A1AA' }}>{sel.styleName} · {sel.supplier}{sel.catalog === 'sample' ? ' (sample catalog)' : ''} · sizes {(sel.sizes || []).join(' ')}</div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#A1A1AA' }}>
          <span>Choose the colors to sell ({picked.length} of {(sel.colors || []).length}){verifying && <span data-testid="catalog-verifying"> · <Loader2 size={11} className="animate-spin" style={{ display: 'inline', verticalAlign: 'middle' }} /> checking photos…</span>}</span>
          <span><button type="button" onClick={() => setPicked((sel.colors || []).map((c) => c.colorName))} style={{ ...btn, padding: '2px 8px', fontSize: 11 }}>All</button> <button type="button" onClick={() => setPicked([])} style={{ ...btn, padding: '2px 8px', fontSize: 11 }}>None</button></span>
        </div>
        <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))', maxHeight: 300, overflowY: 'auto' }}>
          {(sel.colors || []).map((c) => {
            const on = picked.includes(c.colorName);
            return (
              <button type="button" key={c.colorName} onClick={() => toggle(c.colorName)} data-testid={`catalog-color-${c.colorName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                style={{ ...btn, flexDirection: 'column', padding: 6, gap: 4, borderColor: on ? '#4F46E5' : '#3F3F46', background: on ? '#1E1B4B' : '#18181B', position: 'relative' }}>
                {c.imageUrl ? <img src={c.imageUrl} alt={c.colorName} loading="lazy" style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 4, background: '#fff' }} />
                  : <span style={{ width: '100%', aspectRatio: '1', borderRadius: 4, background: c.hex || '#555', display: 'block' }} />}
                <span style={{ fontSize: 11, lineHeight: 1.2, textAlign: 'center' }}>{c.colorName}</span>
                {on && <Check size={12} style={{ position: 'absolute', top: 4, right: 4, background: '#4F46E5', borderRadius: 999, padding: 1 }} />}
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" style={{ ...btnPrimary, opacity: verifying ? 0.6 : 1 }} disabled={verifying} onClick={confirm} data-testid="catalog-use-garment"><Check size={14} /> {verifying ? 'Checking photos…' : 'Use this garment'}</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 8 }} data-testid="catalog-search">
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: '#71717A' }} />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Style # (PC54, G500), brand (Gildan) or description (performance polo)" style={{ ...input, paddingLeft: 30 }} data-testid="catalog-search-input" />
        </div>
        {onCancel && <button type="button" style={btn} onClick={onCancel} data-testid="catalog-cancel">Cancel</button>}
      </div>
      {loading && <div style={{ fontSize: 13, color: '#A1A1AA', display: 'flex', gap: 6, alignItems: 'center' }}><Loader2 size={13} className="animate-spin" /> Searching S&S Activewear and SanMar…</div>}
      {!loading && q.trim().length >= 2 && rows.length === 0 && <div style={{ fontSize: 13, color: '#71717A' }} data-testid="catalog-empty">No garments matched “{q}”.</div>}
      <div style={{ display: 'grid', gap: 6, maxHeight: 340, overflowY: 'auto' }}>
        {rows.map((r) => (
          <button type="button" key={`${r.supplier}-${r.styleID}`} onClick={() => choose(r)} data-testid={`catalog-result-${String(r.styleID).toLowerCase()}`}
            style={{ ...btn, justifyContent: 'flex-start', textAlign: 'left', padding: 8, gap: 10 }}>
            <div style={{ width: 44, height: 44, borderRadius: 6, background: '#fff', flexShrink: 0, overflow: 'hidden' }}>{r.styleImageUrl && <img src={r.styleImageUrl} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}</div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.brandName} {r.styleID} <span style={{ fontWeight: 400, color: '#A1A1AA' }}>— {r.styleName}</span></div>
              <div style={{ fontSize: 11, color: '#A1A1AA' }}>{r.supplier}{r.catalog === 'sample' ? ' · sample' : ''} · {(r.colors || []).length} colors · {(r.sizes || []).join(' ')}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
